import {
  Image,
  Upload,
  UploadState,
  ImageMetaData,
  ProcessingError,
  ResultData,
  ProcessingState,
  UploadError,
  MagicBackgroundResult,
} from "./image";
import originalPlaceholder from "@assets/images/inline-editor/original_placeholder.jpg";

class ResponseParser {
  static parse(rawData: any): Image[] {
    const images: Image[] = rawData.data.map((entry: any) => {
      const image: Image = {
        meta: this.parseMetaData(entry),
        original: this.parseOriginalData(entry.original),
        previewResult: this.parseProcessingData(entry.preview_result),
        hdResult: this.parseProcessingData(entry.hd_result),
        magicBrushPreviewResult: this.parseProcessingData(entry.magic_brush_preview_result),
        magicBrushHdResult: this.parseProcessingData(entry.magic_brush_hd_result),
        magicBackgroundResults: this.parseMagicBackgroundResults(entry.magic_background_results),
      };
      return image;
    });

    return images;
  }

  private static parseMetaData(entry: any): ImageMetaData {
    const meta = entry.meta;

    // Use a placeholder image for the original image if it is not yet uploaded
    let preview = originalPlaceholder;
    if (entry.original.state === UploadState.Finished) {
      preview = entry.original.url;
    }

    let hdWidth = meta["50_mp_width"] || meta.hd_width;
    let hdHeight = meta["50_mp_height"] || meta.hd_height;

    const metaData: ImageMetaData = {
      id: meta.id,
      self: meta.self,
      preview: preview,
      livePreview: preview,
      foregroundType: meta.foreground_type,
      modelsVersion: meta.models_version,
      previewWidth: meta.preview_width,
      previewHeight: meta.preview_height,
      hdWidth: hdWidth,
      hdHeight: hdHeight,
      fullWidth: meta.hd_width,
      fullHeight: meta.hd_height,
      fullAvailable: meta.full_available,
      orientation: meta.orientation,
      ai_brush_processing_full_count: meta.ai_brush_processing_full_count,
      ai_brush_iterations: meta.ai_brush_iterations,
      ai_brush_last_iteration: meta.ai_brush_last_iteration,
      has_no_foreground: meta.has_no_foreground,
      magicBackgroundPreviewWidth: meta.magic_background_preview_width,
      magicBackgroundPreviewHeight: meta.magic_background_preview_height,
      magicBackgroundHdWidth: meta.magic_background_hd_width,
      magicBackgroundHdHeight: meta.magic_background_hd_height,
    };

    return metaData;
  }

  private static parseOriginalData(original: any): Upload | UploadError {
    if (original.state === UploadState.Error) {
      return {
        state: UploadState.Error,
        code: original.error_code,
        message: original.error_message,
      };
    }

    if (original.state === UploadState.Uploading) {
      return {
        state: UploadState.Uploading,
      };
    }

    const uploadData: Upload = {
      state: UploadState.Finished,
      url: original.url,
    };

    return uploadData;
  }

  private static parseProcessingData(processingData: any): ResultData | ProcessingError {
    if (processingData.state === ProcessingState.Error) {
      return this.parseErrorData(processingData);
    }

    const resultData: ResultData = {
      state: processingData.state,
      url: processingData.url,
      name: processingData.name,
      orderedAt: processingData.ordered_at,
      rated: processingData.rated,
      rating: processingData.rating,
      contributed: processingData.contributed,
      nextFetchIn: processingData.next_fetch_in,
      templateId: processingData.template_id,
      iterationId: processingData.iteration_id,
    };

    return resultData;
  }

  private static parseMagicBackgroundResults(magicBackgroundResults: any): Map<string, MagicBackgroundResult> {
    const magicBackgroundResultsMap = new Map<string, MagicBackgroundResult>();

    if (!magicBackgroundResults) {
      return magicBackgroundResultsMap;
    }

    for (const [key, value] of Object.entries(magicBackgroundResults)) {
      magicBackgroundResultsMap.set(key, this.parseMagicBackgroundResult(value));
    }

    return magicBackgroundResultsMap;
  }

  private static parseMagicBackgroundResult(magicBackgroundResult: any): MagicBackgroundResult {
    return {
      magicBackgroundPreviewResult: this.parseProcessingData(magicBackgroundResult.magic_background_preview_result),
      magicBackgroundHdResult: this.parseProcessingData(magicBackgroundResult.magic_background_hd_result),
      foregroundHdResult: this.parseProcessingData(magicBackgroundResult.foreground_hd_result),
    };
  }

  private static parseErrorData(error: any): ProcessingError {
    const processingError: ProcessingError = {
      state: ProcessingState.Error,
      code: error.error_code,
      message: error.error_message,
    };

    return processingError;
  }
}

export default ResponseParser;
